/**
 * Date and time formatting utilities
 */
// Синглтон i18next, а НЕ '../../i18n/config': импорт конфига несёт побочный эффект
// (`i18n.use(initReactI18next).init(...)`), и утилита форматирования дат протащила бы его
// в каждый модуль, который её зовёт, — 16 наборов тестов упали на инициализации i18n.
// Конфиг настраивает этот же экземпляр, так что `i18n.language` здесь тот же самый.
import i18n from 'i18next';

/**
 * Активная локаль интерфейса.
 *
 * ⚠️ Раньше этот модуль собирал `DD.MM.YYYY` строкой вручную, а 54 места по коду звали
 * `toLocaleDateString('ru-RU')` напрямую. И то и другое означает: в английской локали дата
 * всё равно выйдет русской. «Правильный» путь через этот помощник был не лучше зашитого —
 * поэтому чинится он сам, а не только вызовы (цикл 70).
 *
 * Берём язык из ЖИВОГО i18n, а не из снимка при импорте: `changeLanguage` переключает его
 * на лету, и модуль, запомнивший значение при загрузке, продолжит форматировать по-старому.
 * Образец — `CustomerActivityLog`, где `i18n.language` уже читается на каждый рендер.
 */
export function currentLocale(): string {
  return i18n.language || 'ru';
}

/**
 * Format date to DD.MM.YYYY
 * @param date - Date object
 * @returns Formatted date string
 */
export function formatDate(date: Date): string {
  if (!date) return '';
  return new Intl.DateTimeFormat(currentLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/**
 * Format time to HH:MM:SS
 * @param date - Date object
 * @returns Formatted time string
 */
export function formatTime(date: Date): string {
  if (!date) return '';
  return new Intl.DateTimeFormat(currentLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * Format date and time to DD.MM.YYYY HH:MM:SS
 * @param date - Date object
 * @returns Formatted date and time string
 */
export function formatDateTime(date: Date): string {
  return `${formatDate(date)} ${formatTime(date)}`;
}

/**
 * Разобрать строку момента времени с провода.
 * `new Date('мусор')` не бросает, а возвращает Invalid Date — без этой проверки
 * форматтеры печатали бы `NaN.NaN.NaN`, поэтому try/catch тут недостаточно.
 * @param isoString - ISO 8601 date string
 * @returns Date или null, если строку разобрать не удалось
 */
function parseISO(isoString: string): Date | null {
  if (!isoString) return null;
  const parsed = new Date(isoString);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Format ISO string to readable format
 * @param isoString - ISO 8601 date string
 * @returns Formatted date and time string
 */
export function formatISODateTime(isoString: string): string {
  const parsed = parseISO(isoString);
  return parsed ? formatDateTime(parsed) : isoString;
}

/**
 * Format ISO string to date only
 * @param isoString - ISO 8601 date string
 * @returns Formatted date string
 */
export function formatISODate(isoString: string): string {
  const parsed = parseISO(isoString);
  return parsed ? formatDate(parsed) : isoString;
}

/**
 * Format ISO string to time only
 * @param isoString - ISO 8601 date string
 * @returns Formatted time string
 */
export function formatISOTime(isoString: string): string {
  const parsed = parseISO(isoString);
  return parsed ? formatTime(parsed) : isoString;
}

/**
 * Get relative time (e.g., "2 hours ago")
 * @param date - Date object
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'сейчас';
  } else if (diffMins < 60) {
    return `${diffMins}м назад`;
  } else if (diffHours < 24) {
    return `${diffHours}ч назад`;
  } else if (diffDays < 30) {
    return `${diffDays}д назад`;
  }

  return formatDate(date);
}

/**
 * Format bytes to human-readable size
 * @param bytes - Number of bytes
 * @returns Formatted size string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
